import { useState, ReactNode, MouseEvent } from "react";
import { Tooltip, Typography, Box, Button, ClickAwayListener } from "@mui/material";

interface ExpandableTooltipProps {
  text: string;
  children: ReactNode;
  placement?: "bottom" | "left" | "right" | "top";
  className?: string;
}

const NOTE_ITEM_COMPLETED_CLASS = "note-item-completed";

function classNameContainsNoteItemCompleted(className?: string): boolean {
  if (className == null || !String(className).trim()) return false;
  return String(className)
    .trim()
    .split(/\s+/)
    .includes(NOTE_ITEM_COMPLETED_CLASS);
}

const ExpandableTooltip = ({ text, children, placement = "bottom", className }: ExpandableTooltipProps) => {
  const [expanded, setExpanded] = useState(false);
  const [open, setOpen] = useState(false);
  const [clickedInside, setClickedInside] = useState(false);
  const charLimit = 100; // Limit for truncation

  const isCompletedNoteTooltip = classNameContainsNoteItemCompleted(className);

  const isTruncated = text.length > charLimit;
  let displayedText;
  if (isTruncated) {
    displayedText = expanded ? text : `${text.slice(0, charLimit)}`;
  } else {
    displayedText = text; // Show full text if not truncated
  }

  const handleToggle = (e: MouseEvent) => {
    e.stopPropagation(); // Prevent closing when clicking inside
    setExpanded((prev) => !prev);
    setOpen(true); // Keep tooltip open after clicking
    setClickedInside(true);
  };

  const handleTooltipOpen = () => {
    if (!clickedInside) {
      setExpanded(false);
      setOpen(true);
    }
  };

  const handleTooltipClose = () => {
    setOpen(false);
    setClickedInside(false);
  };

  return (
    <ClickAwayListener onClickAway={handleTooltipClose}>
      <Box
        display="inline-block"
        onMouseEnter={handleTooltipOpen}
        onMouseLeave={() => {
          if (!clickedInside) setOpen(false);
        }}
      >
        <Tooltip
          open={open}
          placement={placement}
          PopperProps={{
            modifiers: [
              {
                name: 'preventOverflow',
                options: {
                  boundary: 'viewport',
                  padding: 8,
                },
              },
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['left', 'top', 'bottom', 'right'],
                },
              },
            ],
          }}
          title={
            <Box onClick={(e) => e.stopPropagation()}>
              <Typography
                sx={{
                  fontFamily: "Cerebri Sans",
                  fontSize: "14px",
                  fontWeight: 400,
                  backgroundColor: "#FFFFFF",
                  color: "#000",
                  borderRadius: "4px",
                  maxWidth: "274px",
                  ...(isCompletedNoteTooltip ? { opacity: 0.5 } : {}),
                }}
                dangerouslySetInnerHTML={{ __html: displayedText }}
              >
              </Typography>
              {isTruncated && (
                <Button
                  sx={{
                    fontSize: "14px",
                    color: "#A5684D",
                    textTransform: "none",
                    padding: 0,
                    fontWeight: 500,
                    textDecoration: "underline",
                  }}
                  onClick={handleToggle}
                >
                  {expanded ? "See less" : "See more"}
                </Button>
              )}
            </Box>
          }
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#FFFFFF",
                color: "#000",
                boxShadow: "0px 2px 4px 0px #00000026",
                paddingY: "12px",
                paddingX: "16px",
                maxWidth: "274px",
              },
            },
            arrow: {
              sx: {
                color: "#FFFFFF",
              },
            },
          }}
          arrow
        >
          <span>{children}</span>
        </Tooltip>
      </Box>
    </ClickAwayListener>
  );
};

export default ExpandableTooltip;
